import React, { useState, useEffect } from "react";

import { useLocation } from "react-router-dom";

import backendClient from "src/api";
import { AuthenticationMethodEnum, type MyLoginConfig } from "src/client";
import FormikField from "src/components/common/FormikField";
import MaskedTextField from "src/components/common/MaskedTextField";
import { Alert } from "src/components/ui/alert";
import { Button } from "src/components/ui/button";
import LoginLayout from "src/experiences/auth/LoginLayout";
import { AzureLoginButton } from "src/experiences/auth/common/AzureButton";
import { MagicLinkButton } from "src/experiences/auth/common/MagicLink";
import { OIDCLoginButton } from "src/experiences/auth/common/OIDCButton";
import { useAzureSSO } from "src/experiences/auth/hooks/useAzureSSO";
import { useForgotPassword } from "src/experiences/auth/hooks/useForgotPass";
import { usePassLogin } from "src/experiences/auth/hooks/usePassLogin";
import { useSendSetupEmail } from "src/experiences/auth/hooks/usePassSetup";
import ConsentText from "src/experiences/auth/MultiAuthFlow/LegalTerms";
import useApiError from "src/hooks/useApiError";
import useForm from "src/hooks/useForm";
import yup from "src/utils/validations";

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required."),
  password: yup.string(),
});

const Login = () => {
  const { error, handleApiError, clearErrors } = useApiError();
  const [myLoginConfig, setMyLoginConfig] = useState<MyLoginConfig | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const location = useLocation();

  const fetchMyLoginConfig = (email: string) => {
    setLoading(true);
    backendClient.default
      .getMyLoginConfig(email)
      .then((myLoginConfig) => {
        setMyLoginConfig(myLoginConfig);
        setFormSubmitted(true);
      })
      .catch((error) => {
        handleApiError(error);
        setFormSubmitted(false);
        setMyLoginConfig(null);
      })
      .finally(() => setLoading(false));
  };

  const { initiateAzureSSO, loading: azureLoading } = useAzureSSO();
  const handleLogin = usePassLogin();
  const handleForgotPass = useForgotPassword();
  const sendSetupEmail = useSendSetupEmail();

  const formik = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const trimmedEmail = values.email.trim();
      try {
        clearErrors();
        if (!formSubmitted) {
          fetchMyLoginConfig(trimmedEmail);
        } else {
          if (myLoginConfig?.hasPassword) {
            await handleLogin(
              values.password,
              trimmedEmail,
              myLoginConfig.authSettings,
              handleApiError
            );
          } else {
            await sendSetupEmail(trimmedEmail);
          }
        }
      } catch (error) {
        handleApiError(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get("email");
    if (emailParam) {
      formik.setFieldValue("email", emailParam);
      fetchMyLoginConfig(emailParam);
    }
  }, [location]);

  // Function to handle going back to email step
  const handleChangeEmail = () => {
    setFormSubmitted(false);
    setMyLoginConfig(null);
    clearErrors();
  };

  const hasPasswordAuthMethod =
    myLoginConfig?.authSettings?.authMethods?.includes(
      AuthenticationMethodEnum.PASSWORD
    );

  const hasSSOButtons =
    formSubmitted &&
    (myLoginConfig?.authSettings?.authMethods?.includes(
      AuthenticationMethodEnum.MAGIC_LINK
    ) ||
      myLoginConfig?.authSettings?.authMethods?.includes(
        AuthenticationMethodEnum.AZURE_SSO
      ) ||
      (myLoginConfig?.authSettings?.authMethods?.includes(
        AuthenticationMethodEnum.OIDC
      ) &&
        myLoginConfig?.authSettings?.oidcProvider));

  return (
    <LoginLayout>
      {/* Heading */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold">Sign in</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Enter your email to get started
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <Alert variant="destructive">{error}</Alert>
        </div>
      )}

      <form onSubmit={formik.handleSubmit}>
        <div className="flex flex-col gap-4">
          <div className={formSubmitted ? "[&_input:disabled]:opacity-100 [&_input:disabled]:bg-muted [&_input:disabled]:text-foreground" : undefined}>
            <FormikField
              Component={MaskedTextField}
              name="email"
              formik={formik}
              componentProps={{
                fullWidth: true,
                label: "Email address",
                autoFocus: !formSubmitted,
                disabled: formSubmitted,
                onChange: (e) => {
                  formik.setFieldValue("email", e.target.value.trim());
                },
                inputProps: {
                  "data-1p-ignore": false,
                },
              }}
            />
          </div>
          {(!formSubmitted ||
            (error && !(formSubmitted && myLoginConfig?.hasPassword))) && (
            <Button
              className="w-full bg-[#121212] hover:bg-[#2a2a2a] dark:bg-[#e8e8e8] dark:hover:bg-[#d4d4d4] dark:text-[#121212]"
              size="lg"
              type="submit"
              disabled={loading}
            >
              Continue
            </Button>
          )}
          {formSubmitted && hasPasswordAuthMethod && (
            <div className="animate-fade-in">
              {myLoginConfig?.hasPassword ? (
                <>
                  <div className="flex flex-col gap-4">
                    <FormikField
                      Component={MaskedTextField}
                      name="password"
                      formik={formik}
                      componentProps={{
                        fullWidth: true,
                        label: "Password",
                        type: "password",
                        autoFocus: true,
                        inputProps: {
                          "data-1p-ignore": false,
                        },
                      }}
                    />
                    <Button
                      className="w-full"
                      size="lg"
                      type="submit"
                      disabled={loading}
                    >
                      Sign in
                    </Button>
                    <div className="flex items-center justify-between">
                      <Button
                        onClick={handleChangeEmail}
                        variant="ghost"
                        size="sm"
                      >
                        Change email
                      </Button>
                      <Button
                        onClick={() => handleForgotPass(myLoginConfig.email)}
                        variant="ghost"
                        size="sm"
                      >
                        Forgot password?
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <Button
                  className="w-full"
                  size="lg"
                  type="submit"
                  disabled={loading}
                >
                  Set up password
                </Button>
              )}
            </div>
          )}

          {/* SSO divider + buttons */}
          {hasSSOButtons && (
            <>
              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    or
                  </span>
                </div>
              </div>

              {myLoginConfig?.authSettings?.authMethods?.includes(
                AuthenticationMethodEnum.MAGIC_LINK
              ) && <MagicLinkButton email={myLoginConfig.email} />}

              {myLoginConfig?.authSettings?.authMethods?.includes(
                AuthenticationMethodEnum.AZURE_SSO
              ) && <AzureLoginButton />}

              {myLoginConfig?.authSettings?.authMethods?.includes(
                AuthenticationMethodEnum.OIDC
              ) &&
                myLoginConfig?.authSettings?.oidcProvider && (
                  <OIDCLoginButton
                    provider={myLoginConfig.authSettings.oidcProvider}
                  />
                )}
            </>
          )}

          <ConsentText />
        </div>
      </form>
    </LoginLayout>
  );
};

export default Login;
